const HYDRATE_EVENT = 'NOTIFY_ME_SLIDEOUT_HYDRATE'

class NotifyMeSlideout extends HHBaseElement {
  constructor() {
    super()

    this.productForm = this.querySelector('#slideout-notify-me-form')
    this.emailInput = this.querySelector('#slideout-notify-me-email')
    this.skuInput = this.querySelector('#slideout-notify-me-sku')
    this.productIdInput = this.querySelector('#slideout-notify-me-product-id')
    this.variantIdInput = this.querySelector('#slideout-notify-me-variant-id')
    this.newsletterInput = this.querySelector('#slideout-notify-me-nesletter')
    this.atcButton = this.querySelector('.js-slideout-notify-me-atc')
    this.confirmation = this.querySelector(
      '.js-slideout-notify-me-confirmation'
    )
    this.featuredImage = this.querySelector('.js-slideout-notify-me-image')
    this.productTitle = document.querySelector('.js-slideout-notify-me-title')
    this.variantInfo = document.querySelector('.js-slideout-notify-me-variant')
    this.sendNotifyMeRequest = this.sendNotifyMeRequest.bind(this)
    this.hydrateSlideoutContent = this.hydrateSlideoutContent.bind(this)
    this._handleTriggerClick = this._handleTriggerClick.bind(this)
  }

  connectedCallback() {
    this.productForm.addEventListener(
      'submit',
      this.sendNotifyMeRequest.bind(this)
    )
    this.addEventListener(HYDRATE_EVENT, this.hydrateSlideoutContent)
    document.addEventListener('click', this._handleTriggerClick)
    this.setAttribute('initialized', '')
  }

  disconnectedCallback() {
    this.productForm.removeEventListener('submit', this.sendNotifyMeRequest)
    this.removeEventListener(HYDRATE_EVENT, this.hydrateSlideoutContent)
    document.removeEventListener('click', this._handleTriggerClick)
  }

  get copy() {
    return (this._copy ??= JSON.parse(this.dataset.copy))
  }

  // Every opener shares this one slideout, so the mode is read from whichever opened it.
  _handleTriggerClick(event) {
    const opener = event.target.closest(
      '.js-open-slideout[data-target="notify-me"]'
    )
    if (!opener) return
    this.preRelease = opener.hasAttribute('data-pre-release')

    const copy = this.preRelease ? this.copy.preRelease : this.copy.default
    const title = this.closest('slide-panel')?.querySelector('[slot="title"]')
    const checkboxLabel = this.querySelector(
      'label[for="slideout-notify-me-nesletter"] .text'
    )
    // The `t` filter HTML-escapes its output, so it is written back as HTML, as the server render does.
    if (title) title.innerHTML = copy.title
    if (checkboxLabel) checkboxLabel.innerHTML = copy.checkboxLabel
    this.querySelector('.slideout__notify-me-body').innerHTML = copy.body
    this.confirmation.innerHTML = copy.confirmation
    this._toggleConfirmation(false)
  }

  // Swaps the form for the confirmation after a successful sign up; reset on every open.
  _toggleConfirmation(show) {
    this.productForm.hidden = show
    this.confirmation.hidden = !show
    // Hiding the form drops focus from the Sign Up button, so hand it to the confirmation.
    if (show) this.confirmation.focus()
  }

  _klaviyoPost(path, payload) {
    return fetch(
      `https://a.klaviyo.com/client/${path}/?company_id=${window.klaviyoCompanyId}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-06-15' },
        body: JSON.stringify(payload),
      }
    )
  }

  _sendBackInStockSubscription(email, variantId) {
    return this._klaviyoPost('back-in-stock-subscriptions', {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: { data: { type: 'profile', attributes: { email } } },
          channels: ['EMAIL'],
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${variantId}`,
            },
          },
        },
      },
    })
  }

  // Back in Stock only fires on a 0 → in-stock change, which a stocked pre-release variant never makes.
  _sendPreReleaseEvent(email, variantId) {
    return this._klaviyoPost('events', {
      data: {
        type: 'event',
        attributes: {
          properties: {
            ProductID: this.productIdInput.value,
            VariantID: variantId,
            SKU: this.skuInput.value,
          },
          metric: {
            data: { type: 'metric', attributes: { name: 'Pre-Release Sign Up' } },
          },
          profile: { data: { type: 'profile', attributes: { email } } },
          unique_id: `pre-release-${variantId}-${email}`,
        },
      },
    })
  }

  hydrateSlideoutContent(e) {
    const { variantId, variantSku, productId, productTitle, featuredImage } =
      e.detail

    if (variantId && this.variantIdInput) {
      this.variantIdInput.value = variantId
    }
    if (variantSku && this.skuInput) {
      this.skuInput.value = variantSku
    }
    if (productId && this.productIdInput) {
      this.productIdInput.value = productId
    }
    if (productTitle && this.productTitle) {
      this.productTitle.innerHTML = productTitle
    }
    if (featuredImage && this.featuredImage) {
      this.featuredImage.innerHTML = `<img src="${featuredImage}" alt="${productTitle}" />`
    }

    this.renderVariantSwatch(variantId)
  }

  renderVariantSwatch(variantId) {
    if (!this.variantInfo) return

    let variantTitle = ''
    let swatchImage = ''
    let hexCodes = ''

    if (variantId) {
      const swatchInput = document.querySelector(
        `variant-picker input[data-variant-id="${variantId}"]`
      )
      if (swatchInput) {
        variantTitle = swatchInput.dataset.variantTitle || swatchInput.value || ''
        swatchImage = swatchInput.dataset.swatchImage || ''
        hexCodes = swatchInput.dataset.hexCodes || ''
      }
    }

    if (!variantTitle || variantTitle.toLowerCase() === 'default title') {
      this.variantInfo.innerHTML = ''
      return
    }

    let swatchHTML = ''
    if (swatchImage) {
      swatchHTML = `
        <div class="color-select-inner">
          <div class="swatch-color-grid grid-1">
            <span style="background-image: url(${swatchImage}); background-size: cover;"></span>
          </div>
        </div>
      `
    } else if (hexCodes) {
      const codes = hexCodes.split(',').map((c) => c.trim()).filter(Boolean)
      if (codes.length) {
        swatchHTML = `
          <div class="color-select-inner">
            <div class="swatch-color-grid grid-${codes.length}">
              ${codes.map((c) => `<span style="background-color: ${c};"></span>`).join('')}
            </div>
          </div>
        `
      }
    }

    this.variantInfo.innerHTML = `${swatchHTML}${variantTitle}`
  }

  // The pre-release event alone doesn't give email marketing consent, so a launch campaign would skip the profile.
  // The newsletter list ID is a footer section setting, which Liquid can't read outside the footer.
  _subscribeToNewsletter(email) {
    const listId = document.querySelector('footer-newsletter')?.dataset.listId
    if (!listId) return

    return this._klaviyoPost('subscriptions', {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Pre-Release Waitlist',
          profile: { data: { type: 'profile', attributes: { email } } },
        },
        relationships: { list: { data: { type: 'list', id: listId } } },
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Klaviyo subscribe request failed: ${response.status}`)
        }
      })
      .catch((error) => console.error(error))
  }

  async sendNotifyMeRequest(e) {
    e.preventDefault()
    const triggerButton = this.atcButton

    try {
      if (triggerButton) {
        triggerButton.setAttribute('loading', true)
      }

      const email = this.emailInput.value
      const variantId = this.variantIdInput.value

      const response = this.preRelease
        ? await this._sendPreReleaseEvent(email, variantId)
        : await this._sendBackInStockSubscription(email, variantId)

      if (!response.ok) {
        throw new Error(`Klaviyo request failed: ${response.status}`)
      }

      if (this.newsletterInput.checked) {
        if (this.preRelease) await this._subscribeToNewsletter(email)
        window._klOnsite = window._klOnsite || []
        window._klOnsite.push(['identify', { email: email }])
      }

      this._toggleConfirmation(true)
    } catch (error) {
      console.error(error)
    } finally {
      if (triggerButton) {
        triggerButton.removeAttribute('loading')
      }
    }
  }
}

window.customElements.define('notify-me-slideout', NotifyMeSlideout)
